import ReactGA from 'react-ga4';
import { DEV_MODE_KEY, GA_TRACKING_ID } from '@/constants/analytics';

/**
 * Categories for analytics events
 */
export type EventCategory =
  | 'User Interaction'
  | 'Navigation'
  | 'Content'
  | 'Form'
  | 'Error'
  | 'Performance';

/**
 * Verify if analytics are enabled
 * By default, they are disabled and only explicitly enabled
 * in production through the environment variable VITE_ANALYTICS_ENABLED
 *
 * Note: The environment variable must be exactly "1" to enable
 * This avoids issues with truthy/falsy strings
 */
export const isAnalyticsEnabled = import.meta.env.VITE_ANALYTICS_ENABLED === "1";

/**
 * Verify if the user has defined themselves as a dev,
 * to avoid loading their behavior in Analytics
 */
export const isDevUser = localStorage.getItem(DEV_MODE_KEY) === "1";

/**
 * GA4 Tracking ID to initialize Google Analytics
 */
export { GA_TRACKING_ID };

/**
 * Helper function to track events using GA4
 * @param {EventCategory} category - Category of the event
 * @param {string} action - Action performed
 * @param {string} [label] - Additional label for the event
 * @param {number} [value] - Numeric value associated with the event
 *
 * @example
 * trackEvent('User Interaction', 'click_button', 'submit');
 * trackEvent('Navigation', 'route_change', '/home');
 */
export const trackEvent = (
  category: EventCategory,
  action: string,
  label?: string,
  value?: number
): void => {
  if (isAnalyticsEnabled && !isDevUser) {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
  }
};
